import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  Box
} from "@mui/material";
import { Button } from "../atoms";
import { useSearchIksTerminalRequest, IksTerminalTerminal, SearchIksTerminalRequestResponse } from "../../hooks/iks/useSearchIksTerminalRequest"; // Updated import
import { useSetSnackBar } from "../../store/Snackbar.state";

interface IksQueryModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialReferenceCode?: string; // New prop
  onBack?: () => void; // New prop
}

export const IksQueryModal = ({ isOpen, onClose, initialReferenceCode, onBack }: IksQueryModalProps) => {
  const [referenceCode, setReferenceCode] = useState("");
  const [queryResult, setQueryResult] = useState<IksTerminalTerminal[] | null>(null);
  const setSnackbar = useSetSnackBar();

  const { data: searchData, isLoading: isSearching, isFetching, refetch } = useSearchIksTerminalRequest(referenceCode, {
    enabled: false,
    onSuccess: (data: SearchIksTerminalRequestResponse) => {
      if (data.isSuccess && data.data && data.data.terminals && data.data.terminals.length > 0) {
        setQueryResult(data.data.terminals);
        setSnackbar({
          severity: "success",
          description: "Sorgulama başarılı.",
          isOpen: true,
        });
      } else {
        setQueryResult(null);
        setSnackbar({
          severity: "error",
          description: data.message || "Kayıt bulunamadı.",
          isOpen: true,
        });
      }
    },
    onError: (error: any) => {
      setQueryResult(null);
      setSnackbar({
        severity: "error",
        description: "Sorgulama sırasında bir hata oluştu.",
        isOpen: true,
      });
    },
  });

  // Effect to handle initialReferenceCode
  React.useEffect(() => {
    if (isOpen && initialReferenceCode) {
      setReferenceCode(initialReferenceCode);
      // We need to wait for state update to trigger refetch, but refetch uses current scope 'referenceCode' which is closure stale?
      // Actually useSearchIksTerminalRequest uses the `referenceCode` passed to it.
      // But we passed `referenceCode` state to the hook.
      // So we need to set state, and let the hook pick it up?
      // No, `refetch` will use the key or the function. The function `() => searchIksTerminalRequest(referenceCode)` uses the closure variable.
      // Only if we put referenceCode in dependency array of useQuery it would auto fetch if enabled=true.
      // But enabled is false.
      // We can imperatively call the search function or use a ref to hold the latest code.
    } else if (isOpen) {
      // Clear if no initial code
      setReferenceCode("");
      setQueryResult(null);
    }
  }, [isOpen, initialReferenceCode]);

  // Separate effect to trigger refetch when referenceCode matches initialReferenceCode and isOpen
  React.useEffect(() => {
    if (isOpen && initialReferenceCode && referenceCode === initialReferenceCode) {
      refetch();
    }
  }, [referenceCode, isOpen, initialReferenceCode, refetch]);

  // Effect to clear results when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setQueryResult(null);
    }
  }, [isOpen]);


  const handleQuery = () => {
    if (!referenceCode) {
      setSnackbar({
        severity: "warning",
        description: "Lütfen bir referans kodu girin.",
        isOpen: true,
      });
      return;
    }
    refetch();
  };

  const handleClose = () => {
    setReferenceCode("");
    setQueryResult(null);
    onClose();
  };

  const showLoading = isSearching || isFetching;

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>IKS Sorgulama</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <TextField
            label="Referans Kodu"
            value={referenceCode}
            onChange={(e) => setReferenceCode(e.target.value)}
            fullWidth
          />
          {showLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress />
            </Box>
          )}
          {queryResult && queryResult.length > 0 && !showLoading && (
            <Stack spacing={1} sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: '4px' }}>
              <Typography variant="subtitle1">Sorgu Sonucu:</Typography>
              {queryResult.map((terminal, index) => (
                <Box key={index} sx={{ mb: 1, pb: 1, borderBottom: '1px dashed #e0e0e0' }}>
                  <Typography variant="body2"><strong>Global Merchant ID:</strong> {terminal.globalMerchantId}</Typography>
                  <Typography variant="body2"><strong>PSP Merchant ID:</strong> {terminal.pspMerchantId}</Typography>
                  <Typography variant="body2"><strong>Terminal ID:</strong> {terminal.terminalId}</Typography>
                  <Typography variant="body2"><strong>Referans Kodu:</strong> {terminal.referenceCode}</Typography>
                  <Typography variant="body2"><strong>Durum Kodu:</strong> {terminal.statusCode}</Typography>
                  {terminal.responseCode && <Typography variant="body2"><strong>Yanıt Kodu:</strong> {terminal.responseCode}</Typography>}
                  {terminal.responseCodeExplanation && <Typography variant="body2"><strong>Yanıt Açıklaması:</strong> {terminal.responseCodeExplanation}</Typography>}
                </Box>
              ))}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {onBack && (
          <Button onClick={onBack} text="Geri" variant="outlined" color="primary" sx={{ mr: 'auto' }} />
        )}
        <Button onClick={handleQuery} text="Sorgula" disabled={isSearching} />
        <Button onClick={handleClose} text="Kapat" variant="outlined" />
      </DialogActions>
    </Dialog>
  );
};
